import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { artProxyUrl } from '../artwork-texture.js';
import { vdjColor, vdjBeatChroma, VdjPost, VdjComfort } from './lib/vdjLook.js';
import { formationWorkerSource, formation, assign, computeLayout, KINDS, FORMS } from './lib/drone-formations.js';

// Contrat du rig partagé : interdit. Les drones sont la lumière.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'drones',
  family: 'particles',
  danceability: 0.80,
  requiresTier: 0,
  energyLevel: 0.72,
  bpmMin: 95,
  bpmMax: 140,
  movement: 0.65,
  tonality: 0.80,
  enabled: true
};

// Drones : un spectacle de drones lumineux dans un ciel nocturne - des points
// de lumière qui volent en formation et se réorganisent pour dessiner des
// formes, le titre du morceau et la pochette elle-même, dans le ciel seul.
// Pop, électro, house vocale, hymnes de festival. Règles de
// kiosk-app/CLAUDE.md ; danceability 0,80 -> rythme « party » (R9).
//
// LES FORMATIONS sont calculées dans un Web Worker (R17, lib/drone-formations.js) :
// la pochette échantillonnée en grille, le titre et le tempo rendus en texte
// puis échantillonnés, les formes ; chacune RECADRÉE dans son emplacement du
// ciel (R6 : la bande au-dessus de la pochette DOM, ou les deux bandes de part
// et d'autre - jamais derrière elle, jamais dans une zone d'interface), puis
// ASSIGNÉE aux drones par rangs le long d'une courbe de Hilbert (des drones
// voisins vont vers des cibles voisines : pas de croisement chaotique).
// Le résultat est une texture flottante de N texels (position ou coordonnées
// locales du genre, couleur 8 bits empaquetée dans le 4e flottant), envoyée
// UNE fois par formation.
//
// LE VERTEX SHADER lit les deux formations (actuelle, suivante : deux
// lectures), les anime selon leur genre (la sphère tourne, la nappe ondule, le
// coeur bat, la spirale tourne en 8 temps, le métronome balance), et fait
// voler chaque drone de l'une à l'autre : un retard selon sa distance à
// l'origine de la vague, puis un ARC (Bézier cubique, points de contrôle levés,
// relevés au-dessus de la pochette quand le trajet passe devant elle, et un
// champ qui l'écarte de son bord - copie exacte de trajectoire() du module).
// Rien n'est calculé sur le CPU par image.
//
// LE CIEL est un shader plein écran (R12) : bleu nuit profond (luminance
// <= 0,03 : la seule exception au noir pur) et des étoiles très faibles.
// (La silhouette de collines et de toits en bas du cadre a été retirée sur
// demande le 14/10/2026 : plus de ligne d'horizon ni de sol, le ciel seul.)

const TIER = {
  0: { n: 640, grid: [32, 20], burst: 160 },
  1: { n: 1000, grid: [40, 25], burst: 240 },
  2: { n: 1500, grid: [48, 30], burst: 300 }
};
const TRANSITION_BARS = 2.5;            // a real flight: the whole formation travels, accelerates, brakes
const LAUNCH_BARS = 3;                  // the take-off from below the frame
const SPRITE_PX = 20;                   // le quad d'un drone : halo nul à 8 px, bord à 10 (R12)
const HOVER = 0.007;                    // le tremblé d'un drone en vol stationnaire (GPS, vent)
// LA FORMATION VIVANTE (sur demande, 15/10/2026 : « les drones ne se
// déplacent pas ») : toute la formation dérive, tourne un peu sur son axe
// vertical (vue sous un autre angle) et ondule ; le vent la balance
const YAW = 0.38;                       // rad, l'amplitude du tour (sauf la sphère : elle tourne déjà)
const DRIFT = [0.035, 0.022];           // l'amplitude de la dérive (unités s), dans les marges de 0,07 à 0,12 autour de la pochette
const BOB = 0.010;
const BEAT_PULSE = 0.10;
const SKY = [0.006, 0.012, 0.046];      // luminance 0,013 ; dégradé jusqu'à ~0,025 à l'horizon
const DROP_SCORE_MIN = 0.7, DROP_STALE_MS = 1500;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  try { return new URLSearchParams(location.search).get(name); } catch (e) { return null; }
}


// ------------------------------------------------------------------ shaders

const SKY_VERTEX = `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy * 2.0, 0.0, 1.0); }
`;
const SKY_FRAGMENT = `
  precision highp float;
  uniform float uAspect;
  uniform float uPx;                    // pixels par unité s (hauteur / 2)
  uniform vec2 uLightPos;               // le barycentre des drones (unités s)
  uniform vec3 uLightCol;
  uniform float uLight;
  uniform float uSkyOn;
  varying vec2 vUv;
  void main() {
    vec2 s = (vUv * 2.0 - 1.0) * vec2(uAspect, 1.0);
    // LE CIEL SEUL (sur demande, 14/10/2026 : plus de ligne d'horizon ni de
    // sol) : bleu nuit, un peu plus clair vers le bas, et des étoiles très
    // faibles (une cellule de 3 px sur 600)
    vec3 sky = vec3(${SKY.join(', ')}) * (1.0 + 0.9 * (1.0 - smoothstep(-1.0, 0.6, s.y)));
    vec2 cell = floor(gl_FragCoord.xy / 3.0);
    float hs = fract(sin(dot(cell, vec2(127.1, 311.7))) * 43758.5453);
    if (hs > 0.9983) { vec2 o = gl_FragCoord.xy - cell * 3.0 - 1.5; sky += vec3(0.05, 0.05, 0.06) * (0.5 + 0.5 * fract(hs * 97.0)) * exp(-dot(o, o) * 0.8); }
    gl_FragColor = vec4(sky * uSkyOn, 1.0);
  }
`;

function droneVertex() {
  return `
  precision highp float;
  attribute float aIdx;
  attribute float aSeed;
  uniform sampler2D uFormA;
  uniform sampler2D uFormB;
  uniform float uKindA; uniform vec4 uParA;
  uniform float uKindB; uniform vec4 uParB;
  uniform float uN;
  uniform float uProg;
  uniform vec2 uWaveOrigin;
  uniform vec4 uCover;                  // cx, cy, demi-largeur, demi-hauteur (unités s)
  uniform float uAspect;
  uniform float uTime;
  uniform float uSpin;                  // la sphère : une révolution en 16 temps
  uniform float uSpiral;                // la spirale : une révolution en 8 temps
  uniform float uWavePhase;
  uniform float uBeat;                  // l'enveloppe du temps (0..1)
  uniform float uSwing;                 // le bras du métronome
  uniform float uSweepX;
  uniform float uLight;
  uniform float uPxScale;
  uniform float uYaw;
  uniform vec2 uDrift;
  uniform vec2 uCenA;
  uniform vec2 uCenB;
  varying vec3 vCol;
  varying float vBright;
  vec3 unpack(float w) {
    float r = floor(w / 65536.0); float g = floor((w - r * 65536.0) / 256.0); float b = w - r * 65536.0 - g * 256.0;
    return pow(vec3(r, g, b) / 255.0, vec3(2.2));
  }
  // la pose d'une formation : position (xy) et luminosité de profondeur (z)
  vec3 pose(vec4 t, float kind, vec4 par) {
    if (kind < 0.5) return vec3(t.xy, 1.0);
    if (kind < 1.5) {                                  // SPHÈRE
      bool right = t.x > 2.0;
      vec3 l = vec3(t.x - (right ? 4.0 : 0.0), t.y, t.z);
      float a = uSpin * (right ? -1.0 : 1.0), c = cos(a), s = sin(a);
      l = vec3(c * l.x + s * l.z, l.y, -s * l.x + c * l.z);
      float ct = cos(0.35), st = sin(0.35);
      l = vec3(l.x, ct * l.y - st * l.z, st * l.y + ct * l.z);
      vec2 cen = right ? par.zy : par.xy;
      return vec3(cen + l.xy * par.w, 0.5 + 0.5 * (l.z * 0.5 + 0.5));
    }
    if (kind < 2.5) {                                  // NAPPE : elle ondule au tempo, bat sur le temps
      return vec3(t.x, t.y + par.x * (1.0 + 0.8 * uBeat) * sin(par.y * t.x + uWavePhase), 1.0);
    }
    if (kind < 3.5) {                                  // COEUR : il bat
      bool right = t.x > 2.0;
      vec2 l = vec2(t.x - (right ? 4.0 : 0.0), t.y) * (1.0 + 0.12 * uBeat);
      vec2 cen = right ? par.zy : par.xy;
      return vec3(cen + l * par.w, 1.0);
    }
    if (kind < 4.5) {                                  // SPIRALE : une révolution en 8 temps
      float th = t.y + t.z * uSpiral;
      vec2 cen = t.z > 0.0 ? par.zy : par.xy;
      return vec3(cen + t.x * par.w * vec2(cos(th), sin(th) * 0.92), 1.0);
    }
    // TEMPO : les chiffres fixes, le bras du métronome qui balance
    if (t.z < 0.5) return vec3(t.xy, 1.0);
    float u = t.z - 1.0;
    return vec3(t.xy + par.x * u * vec2(sin(uSwing), cos(uSwing)), 1.0);
  }
  float sm(float e0, float e1, float x) { float k = clamp((x - e0) / (e1 - e0), 0.0, 1.0); return k * k * (3.0 - 2.0 * k); }
  // LA FORMATION VIVANTE : elle tourne un peu sur son axe vertical (autour de
  // son centre, ou de celui de sa moitié), dérive et ondule, d'un bloc. La
  // sphère tourne déjà ; la nappe, qui entoure la pochette, ne fait que dériver
  // un peu (tournée, elle entrerait dans le trou).
  vec3 alive(vec3 q, float kind, vec4 par, vec2 cen) {
    vec2 c = cen;
    if ((kind > 0.5 && kind < 1.5) || (kind > 2.5 && kind < 4.5)) c = abs(q.x - par.x) < abs(q.x - par.z) ? par.xy : par.zy;
    float k = kind > 0.5 && kind < 2.5 ? 0.0 : 1.0;
    float ya = uYaw * k;
    vec2 p = vec2(c.x + (q.x - c.x) * cos(ya), q.y);
    // le côté qui se tourne vers nous est un peu plus lumineux
    float lit = 1.0 + 0.18 * sin(ya) * clamp((q.x - c.x) / 0.6, -1.0, 1.0);
    p += uDrift * (kind > 1.5 && kind < 2.5 ? 0.3 : 1.0);
    p.y += ${BOB.toFixed(3)} * sin(uTime * 1.1 + c.x * 1.7);
    return vec3(p, q.z * lit);
  }
  void main() {
    vec2 tc = vec2((aIdx + 0.5) / uN, 0.5);
    vec4 A = texture2D(uFormA, tc), B = texture2D(uFormB, tc);
    vec3 pa = alive(pose(A, uKindA, uParA), uKindA, uParA, uCenA), pb = alive(pose(B, uKindB, uParB), uKindB, uParB, uCenB);
    // LA VAGUE : un léger retard selon la distance à l'origine ; la formation
    // vole d'un bloc (un retard fort la brouillait sur place, comme un fondu)
    float dd = min(1.0, length(pa.xy - uWaveOrigin) / 3.0);
    float t0 = clamp(uProg * 1.25 - dd * 0.25, 0.0, 1.0);
    float t = t0 * t0 * t0 * (t0 * (t0 * 6.0 - 15.0) + 10.0);     // accélère, croise, freine
    // L'ARC : Bézier cubique, contrôles levés ; au-dessus de la pochette au besoin
    float dist = length(pb.xy - pa.xy);
    float h = 0.08 + 0.35 * dist;
    float c1 = pa.y + h, c2 = pb.y + h;
    float cl = uCover.x - uCover.z - 0.1, cr = uCover.x + uCover.z + 0.1;
    if (uCover.w > 0.0 && max(pa.x, pb.x) > cl && min(pa.x, pb.x) < cr) {
      float H = min(1.5, 1.5 * (uCover.y + uCover.w + 0.1) - 0.25 * (pa.y + pb.y));
      c1 = max(c1, H); c2 = max(c2, H);
    }
    float u = 1.0 - t, b0 = u * u * u, b1 = 3.0 * u * u * t, b2 = 3.0 * u * t * t, b3 = t * t * t;
    vec2 p = vec2(b0 * pa.x + b1 * pa.x + b2 * pb.x + b3 * pb.x, b0 * pa.y + b1 * c1 + b2 * c2 + b3 * pb.y);
    // LE CONTOURNEMENT de la pochette (contourner() du module)
    float hw = uCover.z + 0.02;
    float dx = abs(p.x - uCover.x), dyo = max(abs(p.y - uCover.y) - uCover.w, 0.0);
    if (uCover.w > 0.0 && dx < hw + 0.08 && dyo < 0.08) {
      float w = (1.0 - sm(0.0, 0.08, dyo)) * (1.0 - sm(hw, hw + 0.08, dx));
      float edge = uCover.x + (p.x >= uCover.x ? 1.0 : -1.0) * (hw + 0.01);
      p.x += (edge - p.x) * w;
    }
    // la dérive stationnaire : jamais parfaitement immobiles
    p += ${HOVER.toFixed(4)} * vec2(sin(uTime * 0.7 + aSeed * 6.28) + 0.5 * sin(uTime * 2.3 + aSeed * 40.0), cos(uTime * 0.53 + aSeed * 12.1) + 0.5 * cos(uTime * 1.9 + aSeed * 23.0));
    p.x += 0.006 * sin(uTime * 0.4 + p.y * 3.0);                  // le vent
    vCol = mix(unpack(A.w), unpack(B.w), t);
    // le temps (+10 %), le balayage de la mesure, la profondeur, l'allumage
    float sweep = exp(-pow((p.x - uSweepX) / 0.22, 2.0));
    vBright = mix(pa.z, pb.z, t) * (1.0 + ${BEAT_PULSE.toFixed(2)} * uBeat + 0.7 * sweep) * uLight;
    vCol = mix(vCol, vec3(1.0), 0.35 * sweep);
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
    gl_PointSize = ${SPRITE_PX.toFixed(1)} * uPxScale;
  }
  `;
}

const DRONE_FRAGMENT = `
  precision mediump float;
  uniform float uPxScale;
  varying vec3 vCol;
  varying float vBright;
  void main() {
    // un drone, par sa distance en PIXELS (R1) : coeur de 2 px, halo doux
    // jusqu'à 6 px, NUL à 8 px - le bord du quad est à 10 px (R12)
    float d = length(gl_PointCoord - 0.5) * ${SPRITE_PX.toFixed(1)};
    float core = 1.0 - smoothstep(0.5, 1.5, d);
    float halo = exp(-d / 1.8) * 0.45 * (1.0 - smoothstep(5.0, 8.0, d));
    vec3 c = vCol * (halo + 0.9 * core) + vec3(0.45 * core);
    gl_FragColor = vec4(c * vBright, 1.0);
  }
`;

// LE FEU D'ARTIFICE du drop : des drones qui éclatent radialement, en trois
// bouquets autour de la pochette formée - analytique, aucune lecture
const BURST_VERTEX = `
  precision highp float;
  attribute vec3 aBurst;                // direction (x, y), vitesse
  attribute vec2 aWhich;                // bouquet (0..2), graine
  uniform vec2 uC0; uniform vec2 uC1; uniform vec2 uC2;
  uniform vec3 uK0; uniform vec3 uK1; uniform vec3 uK2;
  uniform float uT;                     // secondes depuis l'éclatement
  uniform float uAspect;
  uniform vec4 uCover;
  uniform float uPxScale;
  varying vec3 vCol;
  varying float vBright;
  void main() {
    float b = aWhich.x;
    vec2 c = b < 0.5 ? uC0 : (b < 1.5 ? uC1 : uC2);
    vec3 k = b < 0.5 ? uK0 : (b < 1.5 ? uK1 : uK2);
    float tt = max(uT - aWhich.y * 0.05, 0.0);
    vec2 p = c + aBurst.xy * aBurst.z * (1.0 - exp(-tt * 2.6)) - vec2(0.0, 0.05 * tt * tt);
    vCol = mix(k, vec3(1.0), 0.3);
    vBright = uT > 0.0 ? exp(-tt / 0.9) * 1.2 : 0.0;
    // jamais derrière la pochette DOM
    if (abs(p.x - uCover.x) < uCover.z + 0.03 && abs(p.y - uCover.y) < uCover.w + 0.03) vBright = 0.0;
    gl_Position = vec4(p.x / uAspect, p.y, 0.0, 1.0);
    gl_PointSize = ${SPRITE_PX.toFixed(1)} * uPxScale;
  }
`;

// ------------------------------------------------------------ le worker partagé
let _worker = null, _workerFailed = false, _nextId = 0;
const _waiting = new Map();
function sharedWorker() {
  if (_worker || _workerFailed) return _worker;
  if (typeof Worker === 'undefined' || typeof Blob === 'undefined' || typeof URL === 'undefined') { _workerFailed = true; return null; }
  try {
    const url = URL.createObjectURL(new Blob([formationWorkerSource()], { type: 'text/javascript' }));
    _worker = new Worker(url);
    _worker.onmessage = (ev) => { const cb = _waiting.get(ev.data.id); if (cb) { _waiting.delete(ev.data.id); cb(ev.data); } };
    _worker.onerror = () => { for (const [id, cb] of _waiting) cb({ id, error: 'worker' }); _waiting.clear(); };
  } catch (e) { _workerFailed = true; _worker = null; }
  return _worker;
}

const MATERIALS = new Map();
function materials() {
  if (MATERIALS.has('m')) return MATERIALS.get('m');
  const sky = new THREE.ShaderMaterial({
    uniforms: { uAspect: { value: 1.78 }, uPx: { value: 360 }, uLightPos: { value: new THREE.Vector2(0, 0.3) }, uLightCol: { value: new THREE.Color(1, 1, 1) }, uLight: { value: 1 }, uSkyOn: { value: 1 } },
    vertexShader: SKY_VERTEX, fragmentShader: SKY_FRAGMENT, depthTest: false, depthWrite: false
  });
  const drones = new THREE.ShaderMaterial({
    uniforms: {
      uFormA: { value: null }, uFormB: { value: null }, uKindA: { value: 0 }, uParA: { value: new THREE.Vector4() }, uKindB: { value: 0 }, uParB: { value: new THREE.Vector4() },
      uN: { value: 1 }, uProg: { value: 1 }, uWaveOrigin: { value: new THREE.Vector2(-2, 0) }, uCover: { value: new THREE.Vector4(0, 0.0855, 0.36, 0.36) },
      uAspect: { value: 1.78 }, uTime: { value: 0 }, uSpin: { value: 0 }, uSpiral: { value: 0 }, uWavePhase: { value: 0 }, uBeat: { value: 0 }, uSwing: { value: 0 },
      uSweepX: { value: -9 }, uLight: { value: 1 }, uPxScale: { value: 1 },
      uYaw: { value: 0 }, uDrift: { value: new THREE.Vector2() }, uCenA: { value: new THREE.Vector2() }, uCenB: { value: new THREE.Vector2() }
    },
    vertexShader: droneVertex(), fragmentShader: DRONE_FRAGMENT,
    transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
  });
  const burst = new THREE.ShaderMaterial({
    uniforms: {
      uC0: { value: new THREE.Vector2() }, uC1: { value: new THREE.Vector2() }, uC2: { value: new THREE.Vector2() },
      uK0: { value: new THREE.Color() }, uK1: { value: new THREE.Color() }, uK2: { value: new THREE.Color() },
      uT: { value: -1 }, uAspect: { value: 1.78 }, uPxScale: { value: 1 }, uCover: { value: new THREE.Vector4(0, 0.0855, 0.36, 0.36) }
    },
    vertexShader: BURST_VERTEX, fragmentShader: DRONE_FRAGMENT,
    transparent: true, blending: THREE.AdditiveBlending, depthTest: false, depthWrite: false
  });
  const m = { sky, drones, burst };
  MATERIALS.set('m', m);
  return m;
}

function floatTexture(N) {
  const t = new THREE.DataTexture(new Float32Array(N * 4), N, 1, THREE.RGBAFormat, THREE.FloatType);
  t.minFilter = THREE.NearestFilter; t.magFilter = THREE.NearestFilter; t.generateMipmaps = false;
  t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping; t.needsUpdate = true;
  return t;
}

// le titre et le tempo rendus en texte (un canevas hors écran, une fois par
// morceau) ; l'échantillonnage se fait dans le worker
function textRaster(text) {
  if (typeof document === 'undefined' || !text) return null;
  const FONT = 110, cv = document.createElement('canvas'), ctx = cv.getContext('2d', { willReadFrequently: true });
  const font = `900 ${FONT}px "Helvetica Neue", Helvetica, Arial, sans-serif`;
  ctx.font = font;
  const w = Math.ceil(ctx.measureText(text).width) + 40, h = Math.ceil(FONT * 1.35);
  cv.width = w; cv.height = h; ctx.font = font; ctx.fillStyle = '#fff'; ctx.textBaseline = 'middle';
  ctx.fillText(text, 20, h / 2);
  return { w, h, data: ctx.getImageData(0, 0, w, h).data };
}
export function titleText(raw) {
  const t = String(raw || '').trim().toUpperCase().replace(/\s+/g, ' ');
  if (!t) return '';
  if (t.length <= 22) return t;
  const cut = t.slice(0, 21), sp = cut.lastIndexOf(' ');
  return (sp > 6 ? cut.slice(0, sp) : cut).replace(/[\s,.;:-]+$/, '') + '…';
}

// ------------------------------------------------------------- le visualiseur

export class DronesVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = TIER[o.qualityTier] ? o.qualityTier : 1;
    this._layout = TIER[this._tier];
    const f = urlParam('droneform');
    this._forcedForm = FORMS.includes(f) ? f : null;
    const t = parseFloat(urlParam('dronet'));
    this._forcedT = Number.isFinite(t) ? clamp01(t) : null;
    this._forcedTitle = urlParam('dronetitle');
    this._probe = urlParam('droneprobe') === '1';
    this._post = new VdjPost({ bloomStrength: 0.5, bloomThreshold: 0.78, grain: 0.012, vignette: 0.35 });
    this._comfort = new VdjComfort();
  }

  get postProcessing() { return this._post.value; }

  init(scene, camera) {
    this._scene = scene;
    const L = this._layout, N = L.n;
    const M = this._m = materials();
    sharedWorker();
    this._group = new THREE.Group();
    scene.add(this._group);
    // le ciel (plein écran, derrière)
    this._sky = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), M.sky);
    this._sky.frustumCulled = false; this._sky.renderOrder = -1000;
    this._sky.onBeforeRender = (renderer) => this.onBeforeRenderSizes(renderer);
    this._group.add(this._sky);
    // les drones : un indice, une graine ; tout le reste dans les textures
    const g = new THREE.BufferGeometry();
    const idx = new Float32Array(N), seed = new Float32Array(N), pos = new Float32Array(N * 3);
    for (let i = 0; i < N; i++) { idx[i] = i; seed[i] = ((i * 0.6180339887) % 1); }
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('aIdx', new THREE.BufferAttribute(idx, 1));
    g.setAttribute('aSeed', new THREE.BufferAttribute(seed, 1));
    this._drones = new THREE.Points(g, M.drones);
    this._drones.frustumCulled = false; this._drones.renderOrder = 10;
    this._group.add(this._drones);
    // le feu d'artifice
    const nb = L.burst, bg = new THREE.BufferGeometry(), bd = new Float32Array(nb * 3), bw = new Float32Array(nb * 2);
    for (let i = 0; i < nb; i++) { const a = i * 2.39996, sp = 0.12 + 0.2 * ((i * 0.7548776) % 1); bd[i * 3] = Math.cos(a); bd[i * 3 + 1] = Math.sin(a); bd[i * 3 + 2] = sp; bw[i * 2] = i % 3; bw[i * 2 + 1] = (i * 0.3819660) % 1; }
    bg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(nb * 3), 3));
    bg.setAttribute('aBurst', new THREE.BufferAttribute(bd, 3));
    bg.setAttribute('aWhich', new THREE.BufferAttribute(bw, 2));
    this._burst = new THREE.Points(bg, M.burst);
    this._burst.frustumCulled = false; this._burst.renderOrder = 11;
    this._group.add(this._burst);
    this._texA = floatTexture(N); this._texB = floatTexture(N);
    M.drones.uniforms.uN.value = N;

    // l'état
    this._time = 0; this._beats = 0; this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null; this._free = 0;
    this._secondsBeat = 0.5; this._paused = false; this._beatAge = 10; this._barAge = 10; this._phrases = 0;
    this._seqIdx = 0; this._kindA = null; this._A = null; this._B = null; this._prog = 1; this._progRate = 0;
    this._pending = null; this._light = 1; this._dropAt = -1e9; this._blackUntil = -1; this._dropCoverWanted = false;
    this._burstAt = -1e9; this._burstCentres = null;
    this._paletteKey = null; this._paletteSrgb = [[1, 0.3, 0.7], [0.3, 0.7, 1]];
    this._coverUrl = undefined; this._cover = null; this._coverLoading = false;
    this._titleKey = undefined; this._titleRaster = null; this._bpmKey = undefined; this._bpmRaster = null;
    this._marksRef = undefined; this._drops = []; this._dropIdx = 0; this._lastNowMs = null; this._lastMode = null;
    this._lastMs = null; this._deferred = null; this._coverWaitSince = null;
    // la première formation, sans transition
    this._first = true;
    if (this._probe && typeof window !== 'undefined') window.__drones = this;
  }

  // --- les entrées des formations -------------------------------------------
  _applyPalette(profile) {
    const pal = profile.artworkPalette;
    const v = pal && pal.vividColors && pal.vividColors.length ? pal.vividColors : [0xff4fa0, 0x3fb6ff, 0xffd23f];
    const key = v.map(String).join('|');
    if (key === this._paletteKey) return;
    this._paletteKey = key;
    // R4 : vdjColor ; transmises en sRGB (le shader repasse en linéaire)
    this._paletteSrgb = v.map((c) => { const k = vdjColor(c).clone().convertLinearToSRGB(); return [k.r, k.g, k.b]; });
  }
  _loadCover(profile) {
    const url = profile.artworkUrl || null;
    if (url === this._coverUrl) return;
    this._coverUrl = url; this._cover = null;
    if (!url || typeof Image === 'undefined' || typeof document === 'undefined') return;
    const [gw, gh] = this._layout.grid, W = gw * 2, H = gh * 2;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    this._coverLoading = true;
    img.onload = () => {
      this._coverLoading = false;
      if (url !== this._coverUrl) return;
      try {
        // un recadrage centré aux proportions de la grille (48 x 30) ; le
        // DÉCODAGE seul ici (quelques ms), la moyenne par drone dans le worker
        const c = document.createElement('canvas'); c.width = W; c.height = H;
        const g = c.getContext('2d', { willReadFrequently: true });
        const ia = img.naturalWidth / img.naturalHeight, ga = W / H;
        let sw = img.naturalWidth, sh = img.naturalHeight, sx = 0, sy = 0;
        if (ia > ga) { sw = sh * ga; sx = (img.naturalWidth - sw) / 2; } else { sh = sw / ga; sy = (img.naturalHeight - sh) / 2; }
        g.drawImage(img, sx, sy, sw, sh, 0, 0, W, H);
        this._cover = { w: W, h: H, data: g.getImageData(0, 0, W, H).data };
      } catch (e) { this._cover = null; }
    };
    img.onerror = () => { this._coverLoading = false; };
    img.src = artProxyUrl(url);
  }
  _texts(profile) {
    const title = titleText(this._forcedTitle != null ? this._forcedTitle : profile.title);
    if (title !== this._titleKey) { this._titleKey = title; this._titleRaster = textRaster(title); }
    const bpm = profile.trackFeatures && profile.trackFeatures.bpm > 0 ? String(Math.round(profile.trackFeatures.bpm)) : '';
    if (bpm !== this._bpmKey) { this._bpmKey = bpm; this._bpmRaster = textRaster(bpm); }
  }
  _available(kind) {
    if (kind === 'title') return !!this._titleRaster;
    if (kind === 'bpm') return !!this._bpmRaster;
    return true;                           // la pochette a un repli (des anneaux)
  }

  _coverRect() {
    // La pochette là où elle EST (sur demande, 15/10/2026, contre R19 qui
    // gardait le bloc au centre de composition) : le trou dans les formations
    // n'existe que quand la pochette est au centre. Sans pochette : aucun trou.
    const sc = safeZoneUniforms.uSafeCenter.value, sh = safeZoneUniforms.uSafeHalfSize.value;
    if (!(sh.x > 0.02 && sh.y > 0.02)) return { cx: 0, cy: 9, hw: 0, hh: 0, none: true };
    return { cx: sc.x, cy: sc.y, hw: sh.x, hh: sh.y };
  }
  _coverKey() { const c = this._coverRect(); return c.none ? 'none' : [c.cx, c.cy, c.hw, c.hh].map((v) => v.toFixed(2)).join(','); }

  // --- les formations ----------------------------------------------------------
  _request(kind, then) {
    // la POCHETTE en cours de décodage : la demande attend (au plus 2 s) -
    // sinon la première formation COVER partait avec le repli en anneaux
    if (kind === 'cover' && this._coverLoading && (this._coverWaitSince == null || this._time - this._coverWaitSince < 2)) {
      if (this._coverWaitSince == null) this._coverWaitSince = this._time;
      this._deferred = { kind, then };
      return;
    }
    this._coverWaitSince = null;
    const o = this._options(kind);
    const prev = this._A ? this._A.screen : null;
    const id = ++_nextId;
    this._pending = { id, kind };
    const fallback = kind === 'cover' && !this._cover;
    const done = (r) => { if (!this._scene || !this._pending || this._pending.id !== id) return; this._pending = null; r.fallback = fallback; then(r); };
    const w = sharedWorker();
    if (w) {
      _waiting.set(id, (m) => { if (m.error) done(this._here(kind, o, prev)); else done(m); });
      w.postMessage({ id, kind, o, prev });
    } else done(this._here(kind, o, prev));
  }
  // les entrées d'une formation (aussi utilisées par le banc Pi, en synchrone)
  _options(kind) {
    const L = this._layout, aspect = safeZoneUniforms.uAspect.value || 1.78;
    return {
      N: L.n, layout: computeLayout(aspect, this._coverRect()), palette: this._paletteSrgb, seed: 7 + this._phrases,
      side: (this._phrases % 2) ? 'right' : 'left', grid: L.grid, cover: this._cover,
      text: kind === 'title' ? this._titleRaster : kind === 'bpm' ? this._bpmRaster : null
    };
  }
  // sans worker : le même calcul sur le fil principal (le budget ne tient plus, mais l'image vient)
  _here(kind, o, prev) {
    const t0 = typeof performance !== 'undefined' ? performance.now() : 0;
    const f = assign(prev, formation(kind, o), o.N);
    return { kind, tex: f.tex, screen: f.screen, par: f.par, ms: (typeof performance !== 'undefined' ? performance.now() : 0) - t0, here: true };
  }
  _upload(tex, data) { tex.image.data.set(data); tex.needsUpdate = true; }
  _setA(r) {
    const U = this._m.drones.uniforms;
    this._upload(this._texA, r.tex);
    this._A = r; U.uKindA.value = KINDS[r.kind]; U.uParA.value.set(r.par[0], r.par[1], r.par[2], r.par[3]);
    U.uCenA.value.copy(this._centre(r.screen));
    this._lightFrom(r);
  }
  _setB(r) {
    const U = this._m.drones.uniforms;
    this._upload(this._texB, r.tex);
    this._B = r; U.uKindB.value = KINDS[r.kind]; U.uParB.value.set(r.par[0], r.par[1], r.par[2], r.par[3]);
    U.uCenB.value.copy(this._centre(r.screen));
  }
  // le centre autour duquel une formation tourne : le milieu de sa boîte
  _centre(sc) {
    let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9; const N = this._layout.n;
    for (let i = 0; i < N; i++) { const x = sc[i * 2], y = sc[i * 2 + 1]; if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
    return new THREE.Vector2((x0 + x1) / 2, (y0 + y1) / 2);
  }
  // LE DÉCOLLAGE : les drones attendent en deux rangs SOUS le cadre, et
  // montent dans la première formation (un spectacle ne commence jamais formé)
  _ground() {
    const N = this._layout.n, aspect = safeZoneUniforms.uAspect.value || 1.78;
    const tex = new Float32Array(N * 4), screen = new Float32Array(N * 2);
    const per = Math.ceil(N / 2), w = 1.8 * aspect;
    for (let i = 0; i < N; i++) {
      const row = i < per ? 0 : 1, k = row ? i - per : i;
      const x = -w / 2 + w * (k + 0.5) / per, y = -1.08 - row * 0.05;
      // lights low on take-off (bright white rows glared through the bloom)
      tex[i * 4] = x; tex[i * 4 + 1] = y; tex[i * 4 + 2] = 0; tex[i * 4 + 3] = 96 * 65536 + 100 * 256 + 128;
      screen[i * 2] = x; screen[i * 2 + 1] = y;
    }
    return { kind: 'cover', tex, screen, par: [0, 0, 0, 1], ground: true };
  }
  // le barycentre et la couleur moyenne : la lumière qui éclaire la silhouette
  _lightFrom(r) {
    const N = this._layout.n; let x = 0, y = 0, cr = 0, cg = 0, cb = 0;
    for (let i = 0; i < N; i++) { x += r.screen[i * 2]; y += r.screen[i * 2 + 1]; const w = r.tex[i * 4 + 3]; cr += Math.floor(w / 65536); cg += Math.floor(w / 256) % 256; cb += w % 256; }
    const S = this._m.sky.uniforms;
    S.uLightPos.value.set(x / N, y / N);
    const k = 1 / (N * 255);
    S.uLightCol.value.setRGB(Math.pow(cr * k, 2.2) * 1.6, Math.pow(cg * k, 2.2) * 1.6, Math.pow(cb * k, 2.2) * 1.6);
  }
  _nextKind() {
    if (this._forcedForm) return this._forcedForm;
    for (let k = 0; k < 12; k++) {
      let kind;
      if (this._seqIdx < FORMS.length) kind = FORMS[this._seqIdx++];
      else { const pool = FORMS.filter((f) => f !== (this._A && this._A.kind)); kind = pool[Math.floor(Math.random() * pool.length)]; }
      if (this._available(kind)) return kind;
    }
    return 'sphere';
  }
  _startTransition(kind) {
    this._request(kind, (r) => {
      this._setB(r);
      this._prog = 0; this._flyDur = null;
      // l'origine de la vague : le bord de la formation actuelle le plus éloigné de la suivante
      this._m.drones.uniforms.uWaveOrigin.value.set(this._A && r.screen ? (this._meanX(this._A.screen) < this._meanX(r.screen) ? -3 : 3) : -3, 0);
    });
  }
  _meanX(s) { let x = 0; const N = this._layout.n; for (let i = 0; i < N; i++) x += s[i * 2]; return x / N; }

  _tickDrops(profile) {
    const tl = profile.analysisTimeline;
    const marks = tl && tl.marks ? tl.marks : null;
    if (marks !== this._marksRef) {
      this._marksRef = marks; this._drops = [];
      if (marks) for (const m of marks) if (m.type === 'drop' && m.score >= DROP_SCORE_MIN) this._drops.push(m.atMs);
      this._dropIdx = 0;
    }
    const now = profile.estimatedPosition != null ? profile.estimatedPosition * 1000 : null;
    if (now == null) return false;
    if (this._lastNowMs != null && (now < this._lastNowMs - 1000 || now > this._lastNowMs + 5000)) this._dropIdx = 0;
    this._lastNowMs = now;
    let fired = false;
    while (this._dropIdx < this._drops.length && this._drops[this._dropIdx] <= now) {
      if (now - this._drops[this._dropIdx] < DROP_STALE_MS) fired = true;
      this._dropIdx++;
    }
    return fired;
  }

  onTrackChange(profile) { this._applyPalette(profile); this._loadCover(profile); this._texts(profile); this._dropIdx = 0; this._lastNowMs = null; }

  update(deltaTime, profile) {
    const dt = Math.min(deltaTime, 0.1);
    this._applyPalette(profile); this._loadCover(profile); this._texts(profile);
    const U = this._m.drones.uniforms, S = this._m.sky.uniforms, Bu = this._m.burst.uniforms;
    if (this._deferred && (!this._coverLoading || this._time - (this._coverWaitSince || 0) >= 2)) { const d = this._deferred; this._deferred = null; this._request(d.kind, d.then); }
    // la pochette arrivée APRÈS une formation COVER de repli : on la refait
    if (this._A && this._A.kind === 'cover' && this._A.fallback && this._cover && !this._pending && this._prog >= 1) {
      this._request('cover', (r) => { this._setB(r); this._prog = 0; this._flyDur = null; });
    }
    // la première formation : SPHÈRE (ou la forme forcée), posée sans transition
    if (this._first && !this._pending) {
      this._first = false;
      let kind = this._forcedForm || FORMS[this._seqIdx++];
      if (!this._available(kind)) kind = 'sphere';
      if (this._forcedForm && this._forcedT != null) {
        // ?dronet : la transition DEPUIS la forme qui la précède dans la séquence
        const prevKind = FORMS[(FORMS.indexOf(kind) + FORMS.length - 1) % FORMS.length];
        const pk = this._available(prevKind) ? prevKind : 'sphere';
        this._request(pk, (r) => { this._setA(r); this._request(kind, (r2) => { this._setB(r2); this._prog = this._forcedT; }); });
      } else {
        // (B = the ground too until the worker answers: an empty B texture put
        // every drone at the origin, a white blob in the middle of the sky)
        const g = this._ground(); this._setA(g); this._setB(g); this._prog = 0; this._flyDur = 'launch';
        this._request(kind, (r) => { this._setB(r); this._prog = 0; this._flyDur = 'launch'; });
      }
    }
    // repli sans BPM : 120
    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondsBeat = bpm > 0 ? 60 / THREE.MathUtils.clamp(bpm, 60, 200) : 0.5;
    const beatS = this._secondsBeat, barS = 4 * beatS;
    this._paused = profile.playbackState !== 'PLAYING';
    if (!this._paused) {
      this._time += dt;
      let beat = false, bar = false, phrase = false;
      const bp = profile.beatPhase;
      if (bp != null) { if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) beat = true; this._phaseBeat = bp; }
      else { this._free += dt; if (this._free >= beatS) { this._free -= beatS; beat = true; } }
      const barPhase = profile.barPhase;
      if (barPhase != null) { if (this._phaseBar != null && barPhase < this._phaseBar - 0.5) bar = true; this._phaseBar = barPhase; }
      else if (beat && (this._beats + 1) % 4 === 0) bar = true;
      const ph = profile.phrasePhase;
      if (ph != null) { if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true; this._phasePhrase = ph; }
      else if (beat && (this._beats + 1) % 16 === 0) phrase = true;
      if (beat) { this._beats++; this._beatAge = 0; }
      if (bar) this._barAge = 0;
      this._beatAge += dt; this._barAge += dt;
      const beatCont = this._beats + (bp != null ? bp : this._free / beatS);

      // DROP : tout s'éteint un temps, puis la POCHETTE d'un coup en pleine
      // lumière, et le feu d'artifice autour
      const director = profile.director;
      let drop = this._tickDrops(profile);
      if (director && director.mode === 'drop' && this._lastMode !== 'drop' && director.hasReliableGrid) drop = true;
      this._lastMode = director ? director.mode : null;
      if (drop && this._time - this._dropAt > 4 * barS && this._A) {
        this._dropAt = this._time; this._blackUntil = this._time + beatS; this._dropCoverWanted = true;
        // un vol en cours se termine vite, dans le noir
        if (this._prog < 1) this._flyDur = 0.45 * beatS;
        this._request('cover', (r) => { this._dropReady = r; });
      }
      // dans le noir, les drones VOLENT jusqu'à la pochette (un vrai spectacle
      // se replace lumières éteintes ; c'était un saut)
      if (this._dropCoverWanted && this._dropReady && this._prog >= 1 && !this._dropArmed) {
        const r = this._dropReady; this._dropReady = null;
        this._setB(r); this._prog = 0; this._flyDur = Math.max(0.25, this._blackUntil - this._time); this._dropArmed = r;
      }
      if (this._dropArmed && this._prog >= 1 && this._time >= this._blackUntil) {
        this._dropCoverWanted = false;
        const r = this._dropArmed; this._dropArmed = null;
        this._light = 1.5;
        // trois bouquets au-dessus et à côté de la pochette formée
        const p = r.par, side = p[3], cx = p[0], cy = p[1];
        // (vers l'EXTÉRIEUR : side vaut -1 à gauche, +1 à droite ; un bouquet
        // tourné vers le centre s'ouvrait derrière la pochette DOM)
        Bu.uC0.value.set(cx + side * 0.15, Math.min(0.42, cy + 0.42)); Bu.uC1.value.set(cx + side * 0.55, cy + 0.1); Bu.uC2.value.set(cx + side * 0.1, cy - 0.32);
        const P = this._paletteSrgb, toLin = (c) => new THREE.Color(c[0], c[1], c[2]).convertSRGBToLinear();
        Bu.uK0.value.copy(toLin(P[0])); Bu.uK1.value.copy(toLin(P[1 % P.length])); Bu.uK2.value.copy(toLin(P[2 % P.length]));
        this._burstAt = this._time;
      }
      // PHRASE : la formation suivante (pas pendant le noir du drop)
      if (phrase && !this._forcedForm && !this._dropCoverWanted && this._prog >= 1 && !this._pending) { this._phrases++; this._startTransition(this._nextKind()); }
      // la pochette a bougé (apparu, disparu) : la formation se réorganise, en vol
      const ck = this._coverKey();
      if (this._layoutKey == null) this._layoutKey = ck;
      if (ck !== this._layoutKey && this._A && !this._A.ground && this._prog >= 1 && !this._pending && !this._dropCoverWanted && !this._forcedForm) {
        this._layoutKey = ck; this._startTransition(this._A.kind);
      }
      // la transition : 2,5 mesures (3 pour le décollage ; celle du drop, dans le noir, tient dans son temps)
      if (this._forcedT == null && this._prog < 1) {
        const dur = this._flyDur === 'launch' ? LAUNCH_BARS * barS : (this._flyDur || TRANSITION_BARS * barS);
        this._prog = Math.min(1, this._prog + dt / dur);
        if (this._prog >= 1) { this._swapAB(); this._flyDur = null; }
      }
      // la formation vivante : un tour lent (16 mesures), une dérive (Lissajous)
      U.uYaw.value = YAW * Math.sin(beatCont / 64 * Math.PI * 2);
      U.uDrift.value.set(DRIFT[0] * Math.sin(beatCont / 48 * Math.PI * 2), DRIFT[1] * Math.sin(beatCont / 40 * Math.PI * 2 + 1));
      // LES ANIMATIONS des formations
      U.uSpin.value = (beatCont / 16 % 1) * Math.PI * 2;
      U.uSpiral.value = (beatCont / 8 % 1) * Math.PI * 2;
      U.uWavePhase.value = (beatCont / 4 % 1) * Math.PI * 2;
      U.uSwing.value = (this._A ? this._A.par[1] || 0.45 : 0.45) * Math.sin(Math.PI * beatCont);
      U.uBeat.value = Math.exp(-this._beatAge / 0.15);
      // la MESURE : un balayage de couleur traverse la formation (la première moitié de la mesure)
      const aspect = safeZoneUniforms.uAspect.value || 1.78;
      const sweepU = this._barAge / (0.5 * barS);
      U.uSweepX.value = sweepU < 1 ? -aspect + 2 * aspect * sweepU : -9;
    }
    // l'allumage : le noir du drop, puis la pleine lumière qui redescend
    // dark from the drop until the cover is formed (the flight happens with the lights off)
    const black = this._time < this._blackUntil || this._dropCoverWanted;
    this._light += (1 - this._light) * (1 - Math.exp(-dt / (2 * beatS)));
    // (rien tant que la première formation n'est pas arrivée du worker :
    // sinon tous les drones s'empilent au centre, derrière la pochette)
    U.uLight.value = black || !this._A ? 0 : this._light;
    S.uLight.value = U.uLight.value;
    Bu.uT.value = this._time - this._burstAt < 4 ? this._time - this._burstAt : -1;
    if (this._forcedT != null) U.uProg.value = this._forcedT; else U.uProg.value = this._prog;
    U.uTime.value = this._time;
    const aspect = safeZoneUniforms.uAspect.value || 1.78;
    const c = this._coverRect();
    U.uCover.value.set(c.cx, c.cy, c.hw, c.hh); Bu.uCover.value.copy(U.uCover.value);
    U.uAspect.value = aspect; S.uAspect.value = aspect; Bu.uAspect.value = aspect;
    U.uFormA.value = this._texA; U.uFormB.value = this._texB;
    this._post.update(this._paused ? 1 : vdjBeatChroma(profile.beatPhase != null ? profile.beatPhase : 0.99, beatS));
  }

  _swapAB() {
    const t = this._texA; this._texA = this._texB; this._texB = t;
    const U = this._m.drones.uniforms;
    this._A = this._B; U.uKindA.value = U.uKindB.value; U.uParA.value.copy(U.uParB.value); U.uCenA.value.copy(U.uCenB.value);
    this._lightFrom(this._A);
  }

  // la taille d'un pixel : le sprite est en pixels du tampon
  onBeforeRenderSizes(renderer) {
    const sz = renderer.getDrawingBufferSize(new THREE.Vector2());
    const k = sz.y / 1080 * 1.0;
    this._m.drones.uniforms.uPxScale.value = Math.max(0.6, Math.min(1.2, sz.y / 720));
    this._m.burst.uniforms.uPxScale.value = this._m.drones.uniforms.uPxScale.value;
    this._m.sky.uniforms.uPx.value = sz.y / 2;
    return k;
  }

  getProbe() {
    return {
      tier: this._tier, n: this._layout.n, kindA: this._A && this._A.kind, kindB: this._B && this._B.kind, prog: this._prog, phrases: this._phrases,
      ms: this._A && this._A.ms, here: this._A && this._A.here, cover: !!this._cover, title: this._titleKey, bpm: this._bpmKey,
      screenA: this._A ? Array.from(this._A.screen) : null, screenB: this._B ? Array.from(this._B.screen) : null,
      parA: this._A && this._A.par, light: this._m.drones.uniforms.uLight.value, rect: this._coverRect()
    };
  }

  dispose() {
    if (this._scene) this._scene.remove(this._group);
    for (const o of [this._sky, this._drones, this._burst]) if (o) o.geometry.dispose();
    for (const t of [this._texA, this._texB]) if (t) t.dispose();
    this._m.drones.uniforms.uFormA.value = null; this._m.drones.uniforms.uFormB.value = null;
    this._pending = null;
    if (typeof window !== 'undefined' && window.__drones === this) window.__drones = null;
    this._scene = null;
  }
}
